import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getAuthApiUrl } from '../lib/apiConfig';

interface UserPreferences {
  filled: boolean;
  gender: 'male' | 'female' | 'unisex';
  interests: string[];
  show_adult_products: boolean;
}

export interface ShippingAddress {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  address_line1: string;
  address_line2: string;
  district: string;
  state: string;
  pincode: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

interface User {
  id: number;
  email: string;
  username: string;
  total_savings?: number;
  total_swipes?: number;
  total_likes?: number;
  // VIP-related fields
  profile?: {
    is_vip: boolean;
    vip_expiry?: string;
    daily_swipes: number;
    last_swipe_date: string;
    total_swipes?: number;
    address?: string;
    phone_number?: string;
  };
  // Preferences
  preferences?: UserPreferences;
  shipping_addresses?: ShippingAddress[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  username: string | null;
  isAuthenticated: boolean;
  login: (token: string, userData: User) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  // VIP-related methods
  isVipUser: () => boolean;
  getRemainingSwipes: () => number | string;
  canSwipe: () => boolean;
  updateDailySwipes: (count: number) => void;
  updateUserProfileFromBackend: (backendData: any) => void;
  refreshUserProfile: () => Promise<User | null>;
  // Preferences-related methods
  hasPreferences: () => boolean;
  updatePreferences: (preferences: UserPreferences) => Promise<void>;
  getPreferences: () => UserPreferences | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [username, setUsername] = useState<string | null>(localStorage.getItem('username'));

  const isAuthenticated = !!token && !!user;

  // VIP-related constants
  const FREE_SWIPE_LIMIT = 10;

  // Define logout first with useCallback (no dependencies needed - setState functions are stable)
  const logout = useCallback(() => {
    // Clear all preference-related session storage
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('preferences_skipped_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    setToken(null);
    setUser(null);
    setUsername(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
  }, []); // No dependencies - setState functions are stable

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await fetch(getAuthApiUrl('/api/users/me/'), {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Update stored username if it's different
        if (userData.username !== username) {
          setUsername(userData.username);
          localStorage.setItem('username', userData.username);
        }
      } else {
        // Token is invalid, clear it
        logout();
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      logout();
    }
  }, [token, username, logout]);

  useEffect(() => {
    // Check if user is already authenticated
    if (token && !user) {
      fetchUserProfile();
    }
  }, [token, user, fetchUserProfile]);

  const login = async (newToken: string, userData: User) => {
    setToken(newToken);
    setUsername(userData.username);
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('username', userData.username);
    
    // Fetch complete user profile including preferences
    try {
      const response = await fetch(getAuthApiUrl('/api/users/me/'), {
        headers: {
          'Authorization': `Token ${newToken}`,
        },
      });

      if (response.ok) {
        const completeUserData = await response.json();
        setUser(completeUserData);
      } else {
        // Fallback to the provided user data if API fails
        setUser(userData);
      }
    } catch (error) {
      console.error('Error fetching complete user profile:', error);
      // Fallback to the provided user data if API fails
      setUser(userData);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
      // Update username if it changed
      if (userData.username && userData.username !== username) {
        setUsername(userData.username);
        localStorage.setItem('username', userData.username);
      }
    }
  };

  // VIP-related methods
  const isVipUser = (): boolean => {
    return user?.profile?.is_vip || false;
  };

  const getRemainingSwipes = (): number | string => {
    if (!user?.profile) return FREE_SWIPE_LIMIT;
    
    if (user.profile.is_vip) return '∞'; // Unlimited for VIP
    
    const dailySwipes = user.profile.daily_swipes || 0;
    return Math.max(0, FREE_SWIPE_LIMIT - dailySwipes);
  };

  const canSwipe = (): boolean => {
    if (!user?.profile) return true; // Default to true if no profile
    
    if (user.profile.is_vip) return true; // VIP can always swipe
    
    const dailySwipes = user.profile.daily_swipes || 0;
    return dailySwipes < FREE_SWIPE_LIMIT;
  };

  const updateDailySwipes = (count: number) => {
    if (user?.profile) {
      setUser({
        ...user,
        profile: {
          ...user.profile,
          daily_swipes: count,
          last_swipe_date: new Date().toISOString().split('T')[0]
        }
      });
    }
  };

  const updateUserProfileFromBackend = (backendData: any) => {
    if (user && backendData) {
      setUser({
        ...user,
        profile: {
          ...user.profile,
          daily_swipes: backendData.profile?.daily_swipes || 0,
          last_swipe_date: backendData.profile?.last_swipe_date || new Date().toISOString().split('T')[0],
          total_swipes: backendData.profile?.total_swipes || 0,
          is_vip: backendData.profile?.is_vip || false,
          vip_expiry: backendData.profile?.vip_expiry
        }
      });
    }
  };

  const refreshUserProfile = async (): Promise<User | null> => {
    if (!token) return null;

    try {
      const response = await fetch(getAuthApiUrl('/api/users/me/'), {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (response.ok) {
        const userData = (await response.json()) as User;
        setUser(userData);
        if (userData.username !== username) {
          setUsername(userData.username);
          localStorage.setItem('username', userData.username);
        }
        return userData;
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('message port closed')) {
        return null;
      }
      console.error('Error refreshing user profile:', error);
    }
    return null;
  };

  // Preferences-related methods
  const hasPreferences = (): boolean => {
    return user?.preferences?.filled || false;
  };

  const updatePreferences = async (preferences: UserPreferences): Promise<void> => {
    const authToken = token ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
    if (!authToken) throw new Error('No authentication token');

    try {
      const response = await fetch(getAuthApiUrl('/api/preferences/update_preferences/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${authToken}`,
        },
        body: JSON.stringify(preferences),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update user state with new preferences
          if (user) {
            setUser({
              ...user,
              preferences: data.data
            });
          }
        } else {
          throw new Error(data.message || 'Failed to update preferences');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update preferences');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  const getPreferences = (): UserPreferences | null => {
    return user?.preferences || null;
  };

  const value: AuthContextType = {
    user,
    token,
    username,
    isAuthenticated,
    login,
    logout,
    updateUser,
    isVipUser,
    getRemainingSwipes,
    canSwipe,
    updateDailySwipes,
    updateUserProfileFromBackend,
    refreshUserProfile,
    hasPreferences,
    updatePreferences,
    getPreferences,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
